import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '@/utils/visa/visa-blank-states'
import {runtimeStore, visaRuntimeStore} from '~/composables/store-accessor'
import type {Ref} from 'vue'
import {isAfter, isBefore, parseISO} from 'date-fns'
import {UTCToDate} from '@/utils/helpers'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import {appInstance} from '@/utils/app-accessor'
import {useProductStore} from '~/composables/product-store'

export const useVisaStore = defineStore('visa', () => {
    const searchRequest: Ref = ref(searchRequestBlank())
    const searchResponse: Ref = ref(searchResponseBlank())
    const filters: Ref = ref(filtersBlank())
    const sortFnName: Ref = ref('priceAsc')
    const groupOffersBy: Ref = ref('none')
    const searchExpirationTime: Ref = ref(null)
    const prepareBookRequest: Ref = ref({})
    const prepareBookResponse: Ref = ref({})
    const basket: Ref<any[]> = ref([])
    const bookingAdditionalOptions: Ref = ref({})

    function _getBasketItem(offerKey: any) {
        return (
            basket.value.find(item => item.offerKey === offerKey) || {
                searchRequest: searchRequestBlank(),
                product: {offers: [], info: {}},
                info: {},
            }
        )
    }

    function ADD_TO_BASKET(offerKey: any) {
        basket.value = basket.value.filter(item => {
            const offer = item.product.offers.find((offer: any) => offer.offerKey === item.offerKey)
            return isBefore(new Date(), UTCToDate(parseISO(offer.expirationTime)))
        })

        if (_getBasketItem(offerKey).offerKey) return

        const product = visaRuntimeStore.offers.find(
            (product: any) => product.offers.findIndex((offer: any) => offer.offerKey === offerKey) !== -1
        )

        basket.value.push({
            offerKey,
            product,
            searchRequest: searchRequest.value,
        })
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = _getBasketItem(offerKey)
        if (!basketItem.offerKey) return
        const offer = basketItem.product.offers.find((offer: any) => offer.offerKey === offerKey)
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)

        offer.currentPrice = price
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function SET_FILTERS(val: any) {
        filters.value = val
    }

    function SET_FILTER(data: any) {
        filters.value[data.key] = data.value
    }

    function RESET_FILTERS() {
        filters.value = filtersBlank()
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_SORT(sort: any) {
        sortFnName.value = sort
    }

    function SET_GROUP_OFFERS_BY(val: any) {
        groupOffersBy.value = val
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP(data: any) {
        prepareBookRequest.value[data.prop] = data.val
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function NEW_SEARCH(_searchRequest: any) {
        searchRequest.value = _searchRequest
        filters.value = filtersBlank()
    }

    function RESET() {
        searchRequest.value = searchRequestBlank()
        prepareBookRequest.value = {}
    }

    async function prepareBook(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_PREPARE_BOOK_REQUEST(rq)
            const response = await appInstance.$api.prepareVisaBook.post(rq)
            SET_PREPARE_BOOK_RESPONSE(response)
            return response
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    const isOffersExpired = computed(() => {
        return () =>
            hasOffers.value && !visaRuntimeStore.searchActive && isAfter(new Date(), searchExpirationTime.value)
    })

    const basketItem = computed(() => {
        return (offerKey: any) => _getBasketItem.call(this, offerKey)
    })

    const hasOffers = computed(() => {
        return !!searchResponse.value.products.length
    })

    function SET_BOOKING_ADDITIONAL_OPTIONS(options: any) {
        if (Object.keys(options).length) {
            bookingAdditionalOptions.value = options
        }
    }

    function clearBookingAdditionalOptions() {
        bookingAdditionalOptions.value = {}
    }

    return {
        ...useProductStore(searchResponse),
        searchRequest,
        searchResponse,
        filters,
        sortFnName,
        groupOffersBy,
        searchExpirationTime,
        prepareBookRequest,
        prepareBookResponse,
        basket,
        bookingAdditionalOptions,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        SET_SEARCH_REQUEST,
        SET_FILTERS,
        SET_FILTER,
        RESET_FILTERS,
        SET_SEARCH_EXPIRATION_TIME,
        SET_SORT,
        SET_GROUP_OFFERS_BY,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_PREPARE_BOOK_RESPONSE,
        NEW_SEARCH,
        RESET,
        prepareBook,
        isOffersExpired,
        basketItem,
        hasOffers,
        SET_BOOKING_ADDITIONAL_OPTIONS,
        clearBookingAdditionalOptions,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useVisaStore, import.meta.hot))
